/**
 * ReportTableCell Component
 *
 * Enhanced cell renderer for report tables that provides styled display
 * based on column metadata and data type.
 */

import React from "react";

/**
 * Enhanced cell renderer that applies styling based on data type
 */
const ReportTableCell = ({ getValue, column }) => {
  const value = getValue();
  const meta = column?.columnDef?.meta;

  // Get the base value
  const baseValue = typeof value === "function" ? value() : value;

  if (baseValue === null || baseValue === undefined) {
    return <span className="text-gray-400">-</span>;
  }

  // Apply styling based on column metadata
  switch (meta?.type) {
    case "boolean":
      const boolValue = Boolean(baseValue);
      return (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            boolValue
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {boolValue ? "Yes" : "No"}
        </span>
      );

    case "currency":
      return (
        <span className="font-medium text-gray-900">
          {typeof baseValue === "number"
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(baseValue)
            : baseValue}
        </span>
      );

    case "number":
      return (
        <span className="font-mono text-sm">
          {typeof baseValue === "number"
            ? baseValue.toLocaleString()
            : baseValue}
        </span>
      );

    case "date":
    case "datetime":
      return <span className="text-sm text-gray-700">{baseValue}</span>;

    case "id":
      return (
        <span className="font-mono text-xs text-gray-500">{baseValue}</span>
      );

    case "array":
      if (Array.isArray(baseValue)) {
        if (baseValue.length === 0) {
          return <span className="text-sm text-gray-400">-</span>;
        }
        return (
          <span className="text-sm text-gray-600">
            {`${baseValue.length} item${baseValue.length !== 1 ? "s" : ""}`}
          </span>
        );
      }
      return <span className="text-sm text-gray-600">-</span>;

    case "object":
      // Handle populated objects (like driver: { firstName: "John", lastName: "Doe" })
      if (baseValue && typeof baseValue === "object") {
        // Try common object display patterns
        if (baseValue.firstName && baseValue.lastName) {
          return (
            <span className="text-sm font-medium text-gray-800">
              {`${baseValue.firstName} ${baseValue.lastName}`}
            </span>
          );
        } else if (baseValue.fullName) {
          return (
            <span className="text-sm font-medium text-gray-800">
              {baseValue.fullName}
            </span>
          );
        } else if (baseValue.name) {
          return (
            <span className="text-sm font-medium text-gray-800">
              {baseValue.name}
            </span>
          );
        } else if (baseValue.email) {
          return (
            <span className="text-sm font-medium text-gray-800">
              {baseValue.email}
            </span>
          );
        } else {
          // Fallback: show a truncated JSON representation
          const jsonStr = JSON.stringify(baseValue);
          return (
            <span
              className="text-sm font-medium text-gray-800"
              title={jsonStr}
            >
              {jsonStr.substring(0, 50)}
              {jsonStr.length > 50 ? "..." : ""}
            </span>
          );
        }
      }
      return <span className="text-sm font-medium text-gray-800">-</span>;

    default:
      // Handle arrays
      if (Array.isArray(baseValue)) {
        if (baseValue.length === 0) {
          return <span className="text-sm text-gray-400">-</span>;
        }
        return (
          <span className="text-sm text-gray-700">
            {`${baseValue.length} item${baseValue.length !== 1 ? "s" : ""}`}
          </span>
        );
      }

      // Handle objects that weren't caught by the "object" case (fallback)
      if (baseValue && typeof baseValue === "object" && !Array.isArray(baseValue)) {
        // Try common object display patterns
        if (baseValue.firstName && baseValue.lastName) {
          return (
            <span className="text-sm text-gray-700">
              {`${baseValue.firstName} ${baseValue.lastName}`}
            </span>
          );
        } else if (baseValue.fullName) {
          return (
            <span className="text-sm text-gray-700">{baseValue.fullName}</span>
          );
        } else if (baseValue.name) {
          return (
            <span className="text-sm text-gray-700">{baseValue.name}</span>
          );
        } else if (baseValue.email) {
          return (
            <span className="text-sm text-gray-700">{baseValue.email}</span>
          );
        }
        // If we can't display it nicely, show a safe representation
        return (
          <span className="text-sm text-gray-500 italic">
            [Object]
          </span>
        );
      }

      const stringValue = String(baseValue);

      // Handle long text with tooltip
      if (stringValue.length > 50) {
        return (
          <span
            title={stringValue}
            className="text-sm text-gray-700 cursor-help"
          >
            {stringValue}
          </span>
        );
      }

      return <span className="text-sm text-gray-700">{stringValue}</span>;
  }
};

export default ReportTableCell;
